import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Pagination,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Login as LoginIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import dayjs, { Dayjs } from 'dayjs';
import {
  activityService,
  UserActivity,
  ActivityStats,
} from '../../services/api/activity.service';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ACTION_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  login: 'success',
  logout: 'default',
  navigate: 'info',
  create: 'primary',
  update: 'warning',
  delete: 'error',
  view: 'default',
  export: 'secondary',
};

function formatDateTime(value: string): string {
  return dayjs(value).format('DD MMM YYYY, HH:mm:ss');
}

function parseDetails(details: string | null): Record<string, unknown> | null {
  if (!details) return null;
  try {
    return typeof details === 'string' ? JSON.parse(details) : details;
  } catch {
    return { raw: details };
  }
}

const UserActivityPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [filterAction, setFilterAction] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [excludeView, setExcludeView] = useState(true);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<UserActivity | null>(null);

  const dateParams = {
    startDate: startDate?.startOf('day').toISOString(),
    endDate: endDate?.endOf('day').toISOString(),
    excludeActions: excludeView ? 'view' : undefined,
  };

  const loadActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await activityService.getActivities({
        page,
        limit: 25,
        action: filterAction || undefined,
        userId: filterUserId || undefined,
        entityType: filterEntityType || undefined,
        ...dateParams,
        sortBy: 'created_at',
        sortOrder: 'DESC',
      });
      setActivities(response.activities);
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.totalItems);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 403 ? 'Admin access required to view activity logs.' : 'Failed to load activity logs.');
    } finally {
      setLoading(false);
    }
  }, [page, filterAction, filterUserId, filterEntityType, startDate, endDate, excludeView]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await activityService.getStats({
        ...dateParams,
        userId: filterUserId || undefined,
      });
      setStats(response);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setError('Admin access required to view activity analytics.');
      }
    } finally {
      setStatsLoading(false);
    }
  }, [startDate, endDate, excludeView, filterUserId]);

  useEffect(() => {
    if (tabIndex === 0) {
      loadActivities();
    }
  }, [tabIndex, loadActivities]);

  useEffect(() => {
    if (tabIndex === 1) {
      loadStats();
    }
  }, [tabIndex, loadStats]);

  const handleRefresh = () => {
    if (tabIndex === 0) loadActivities();
    else loadStats();
  };

  const handleOpenDetail = (activity: UserActivity) => {
    setSelectedActivity(activity);
    setDetailOpen(true);
  };

  const dailyChartData = stats?.dailyActivity.map((d) => ({
    x: dayjs(d.date).format('MMM D'),
    y: d.count,
  })) ?? [];

  const loginChartData = stats?.dailyLogins.map((d) => ({
    x: dayjs(d.date).format('MMM D'),
    y: d.count,
  })) ?? [];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              User Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor user logs and usage analytics across the platform
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading || statsLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="From"
                value={startDate}
                onChange={(val) => { setStartDate(val); setPage(1); }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="To"
                value={endDate}
                onChange={(val) => { setEndDate(val); setPage(1); }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={filterAction}
                  label="Action"
                  onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="login">Login</MenuItem>
                  <MenuItem value="logout">Logout</MenuItem>
                  <MenuItem value="navigate">Navigate</MenuItem>
                  <MenuItem value="create">Create</MenuItem>
                  <MenuItem value="update">Update</MenuItem>
                  <MenuItem value="delete">Delete</MenuItem>
                  <MenuItem value="view">View</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="User ID"
                size="small"
                fullWidth
                value={filterUserId}
                onChange={(e) => { setFilterUserId(e.target.value); setPage(1); }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={filterEntityType}
                  label="Entity Type"
                  onChange={(e) => { setFilterEntityType(e.target.value); setPage(1); }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="page">Page</MenuItem>
                  <MenuItem value="feedback">Feedback</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>API Views</InputLabel>
                <Select
                  value={excludeView ? 'hide' : 'show'}
                  label="API Views"
                  onChange={(e) => { setExcludeView(e.target.value === 'hide'); setPage(1); }}
                >
                  <MenuItem value="hide">Hide API views</MenuItem>
                  <MenuItem value="show">Show all</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ mb: 2 }}>
          <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
            <Tab icon={<HistoryIcon />} iconPosition="start" label="Activity Logs" />
            <Tab icon={<TimelineIcon />} iconPosition="start" label="Analytics" />
          </Tabs>
        </Paper>

        <TabPanel value={tabIndex} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {totalItems.toLocaleString()} activities found
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Entity</TableCell>
                      <TableCell>Target</TableCell>
                      <TableCell align="right">Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activities.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          No activity records found for the selected filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      activities.map((activity) => (
                        <TableRow key={activity.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDateTime(activity.created_at)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{activity.user_email || activity.user_id}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={activity.action}
                              size="small"
                              color={ACTION_COLORS[activity.action] || 'default'}
                            />
                          </TableCell>
                          <TableCell>{activity.entity_type}</TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {activity.entity_id}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" onClick={() => handleOpenDetail(activity)}>
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : stats ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <HistoryIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">Total Activities</Typography>
                      </Box>
                      <Typography variant="h4">{stats.summary.total_activities.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PeopleIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">Unique Users</Typography>
                      </Box>
                      <Typography variant="h4">{stats.summary.unique_users.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LoginIcon color="success" fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">Logins</Typography>
                      </Box>
                      <Typography variant="h4">{stats.summary.total_logins.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <VisibilityIcon color="info" fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary">Page Views</Typography>
                      </Box>
                      <Typography variant="h4">{stats.summary.total_page_views.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: 320 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                      Daily Activity
                    </Typography>
                    {dailyChartData.length > 0 ? (
                      <Box sx={{ height: 260 }}>
                        <ResponsiveLine
                          data={[{ id: 'activity', data: dailyChartData }]}
                          margin={{ top: 10, right: 20, bottom: 40, left: 50 }}
                          xScale={{ type: 'point' }}
                          yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
                          axisBottom={{ tickRotation: -35 }}
                          colors={['#E31837']}
                          pointSize={6}
                          useMesh
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        No daily activity data
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: 320 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                      Daily Logins
                    </Typography>
                    {loginChartData.length > 0 ? (
                      <Box sx={{ height: 260 }}>
                        <ResponsiveBar
                          data={loginChartData}
                          keys={['y']}
                          indexBy="x"
                          margin={{ top: 10, right: 20, bottom: 40, left: 50 }}
                          colors={['#4CAF50']}
                          axisBottom={{ tickRotation: -35 }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        No login data
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Actions Breakdown
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Action</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.actionStats.map((row) => (
                          <TableRow key={row.action}>
                            <TableCell>
                              <Chip label={row.action} size="small" color={ACTION_COLORS[row.action] || 'default'} />
                            </TableCell>
                            <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Top Active Users
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell align="right">Activities</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.userStats.map((row) => (
                          <TableRow key={row.user_id}>
                            <TableCell>{row.user_email}</TableCell>
                            <TableCell align="right">{row.activity_count.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Top Pages Visited
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Page Path</TableCell>
                          <TableCell align="right">Visits</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.topPages.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">No page navigation data yet</TableCell>
                          </TableRow>
                        ) : (
                          stats.topPages.map((row) => (
                            <TableRow key={row.page_path}>
                              <TableCell>{row.page_path}</TableCell>
                              <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>
            </>
          ) : null}
        </TabPanel>

        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Activity Details
            <IconButton size="small" onClick={() => setDetailOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedActivity && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body2"><strong>ID:</strong> {selectedActivity.id}</Typography>
                <Typography variant="body2"><strong>User:</strong> {selectedActivity.user_email || selectedActivity.user_id}</Typography>
                <Typography variant="body2"><strong>Action:</strong> {selectedActivity.action}</Typography>
                <Typography variant="body2"><strong>Entity:</strong> {selectedActivity.entity_type} / {selectedActivity.entity_id}</Typography>
                <Typography variant="body2"><strong>Time:</strong> {formatDateTime(selectedActivity.created_at)}</Typography>
                {selectedActivity.details && (
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Details</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', overflow: 'auto', maxHeight: 240 }}>
                      <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(parseDetails(selectedActivity.details), null, 2)}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default UserActivityPage;
